import FiltersSidepanel from 'common/filters/filters-sidepanel';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Outlet, Route, Routes } from 'react-router-dom';
import { notification } from 'ui';
import { sortCOPSModelToParam } from 'utils/helpers';
import { withRouter } from 'utils/withRouter';
import { boardIdTypes } from '../../common/gantt/config/view';
import { additionalTabTypes, defaultGanttView } from '../../utils/app-configs';
import {
  addDataToNoneView,
  transformFiltersToUrlParams,
  transformUrlParamsToFilters,
} from '../../utils/helpers';
import { resetNotification } from '../containers/planning-board-actions';
import PlanningBoardGantt from '../containers/planning-board-gantt-container';
import PlanningBoardToolbar from '../containers/planning-board-toolbar-container';
import CreateViewSidepanel from '../containers/planning-board-view-creator-container';
import ManageViewsSidepanel from '../containers/planning-board-view-manager-container';
import decideInitialGanttView from '../planning-board-helpers';
import { RouteOptimisationSidePanel } from './planning-board-route-optimisation';

const PlanningBoardTop = ({
  fullScreen = false,
  selectedOrderIds,
  match,
  filterDefinitions,
  isLoadingBoardData,
  activeFilters,
  updateFilters,
  split,
  visible,
  getGanttViews,
  views,
  updateView,
  activeView,
  updateSort,
  saveSuccess,
  intl,
  range,
  updatePeriod,
  planningAndViewToOpen,
  parent,
}) => {
  const dispatch = useDispatch();
  const filterRef = useRef();
  const [prevViews, setPrevViews] = useState([]);

  useEffect(() => {
    getGanttViews({ id: boardIdTypes.service });
  }, []);

  useEffect(() => {
    if (!planningAndViewToOpen?.id) {
      if (activeView?.id) {
        updateFilters(
          activeView.filterData ? transformUrlParamsToFilters(activeView.filterData) : {},
        );
        updateSort(sortCOPSModelToParam(activeView.sorting));
        updatePeriod(activeView.viewRange);
      }
    }
  }, [activeView?.id]);

  useEffect(() => {
    if (planningAndViewToOpen?.id) {
      if (!activeView?.isNone) {
        updateView({
          id: `${boardIdTypes.service}_none`,
          ...defaultGanttView,
        });
      }
    }
  }, [planningAndViewToOpen?.id]);

  // Don't use deepcompare here since views can be null to avoid a rerender
  // Track issue: https://github.com/kentcdodds/use-deep-compare-effect/issues/63
  useEffect(() => {
    if (!saveSuccess && views) {
      const viewsArray = addDataToNoneView(boardIdTypes.service, views);
      const initialView = decideInitialGanttView(viewsArray, match, planningAndViewToOpen);
      updateView(initialView);
      setPrevViews(viewsArray);
    }
  }, [JSON.stringify(views)]);

  useEffect(() => {
    if (saveSuccess) {
      notification.success({
        message: intl.formatMessage({ id: 'worklist.filters.SAVE_VIEW_SUCCESS' }),
        placement: 'top',
      });
      dispatch(resetNotification());

      const viewsArray = addDataToNoneView(boardIdTypes.service, views);

      // If a view is added go to the new view
      if (viewsArray.length === prevViews.length + 1) {
        const addedViews = viewsArray?.filter(view =>
          !prevViews?.find(ev => ev.id === view.id),
        );
        if (addedViews?.length) {
          updateView(addedViews[0]);
          setPrevViews(viewsArray);
          return;
        }
      }

      // Default view has changed
      if (
        viewsArray?.find(v => v.isDefault)?.id !== prevViews?.find(v => v.isDefault)?.id
      ) {
        updateView(viewsArray?.find(v => v.isDefault) || viewsArray?.find(v => v.isNone));
        setPrevViews(viewsArray);
        return;
      }

      // If length is the same.
      // Either the order has changed or a singe view was update.
      // Do nothing in this case
      if (
        viewsArray?.length === prevViews?.id
      ) {
        setPrevViews(viewsArray);
        return;
      }

      // If the activeview is deleted go to default or none
      // else stay on the activeview
      if (!viewsArray?.find(v => v.id === activeView.id)) {
        updateView(viewsArray?.find(v => v.isDefault) || viewsArray?.find(v => v.isNone));
      }
      setPrevViews(viewsArray);
    }
  }, [saveSuccess]);

  return (
    <>
      {visible && <PlanningBoardToolbar fullScreen={fullScreen} split={split} />}
      <PlanningBoardGantt
        selectedOrderIds={selectedOrderIds}
        fullScreen={fullScreen}
        split={split}
        visible={visible}
        parent={parent}
      />
      <Routes>
        <Route
          path={'ganttfilterselect'}
          element={
            <FiltersSidepanel
              filterRef={filterRef}
              isDataLoading={isLoadingBoardData}
              filterDefinitions={filterDefinitions || []}
              updateFilters={updateFilters}
              activeFilters={activeFilters}
              isPlanningBoard={true}
              hasColumnTab={false}
              isGantt={true}
              additionalTabs={[additionalTabTypes.rows]}
            />
          }
        />
        <Route
          path={'create-gantt-view'}
          element={
            <CreateViewSidepanel
              id={boardIdTypes.service}
              filters={transformFiltersToUrlParams(activeFilters) || ''}
              sort={match.params.ganttSort || ''}
              range={range}
              updateView={updateView}
              views={views}
              isGantt={true}
              isPlanningBoard={true}
              expandOption={false}
            />
          }
        />
        <Route
          path={'manage-gantt-views'}
          element={<ManageViewsSidepanel
            id={boardIdTypes.service}
            isGantt={true}
            isPlanningBoard={true}
            expandOption={false}
          />}
        />
        <Route
          path={'route-optimisation'}
          element={<RouteOptimisationSidePanel />}
        />
      </Routes>
      <Outlet />
    </>
  );
};

PlanningBoardTop.propTypes = {
  fullScreen: PropTypes.bool,
  split: PropTypes.bool,
  selectedOrderIds: PropTypes.array,
  match: PropTypes.object,
  filterDefinitions: PropTypes.object,
  updatePeriod: PropTypes.func,
  activeFilters: PropTypes.object,
  updateFilters: PropTypes.func,
  visible: PropTypes.bool,
  getGanttViews: PropTypes.func,
  views: PropTypes.array,
  updateView: PropTypes.func,
  activeView: PropTypes.object,
  saveSuccess: PropTypes.bool,
  intl: PropTypes.object,
  updateSort: PropTypes.func,
  range: PropTypes.object,
  isLoadingBoardData: PropTypes.bool,
  planningAndViewToOpen: PropTypes.object,
  parent: PropTypes.object,
};

export default withRouter(injectIntl(PlanningBoardTop));
